import { GridColDefPro } from './types'

/**
 * Visibilidade de colunas por campo. `true` = visível.
 * Semântica invertida em relação ao `hide` do colDef, para casar com o
 * `columnVisibilityModel` que o projeto já usa.
 */
export type MapaVisibilidade = Record<string, boolean>

const ocultavel = (column: GridColDefPro) => column.hideable !== false

/**
 * Deriva o mapa a persistir a partir do estado atual das colunas.
 * Só entram campos ocultáveis — o usuário não consegue alterar os outros.
 */
export function extrairVisibilidade(
    columns: GridColDefPro[],
): MapaVisibilidade {
    return columns
        .filter(ocultavel)
        .reduce<MapaVisibilidade>((mapa, column) => {
            mapa[column.field] = !column.hide
            return mapa
        }, {})
}

/**
 * Remove do mapa os campos que não existem mais nas colunas ou que deixaram
 * de ser ocultáveis. Chamado no mount para o mapa não acumular lixo entre releases.
 */
export function podarColunasMortas(
    mapa: MapaVisibilidade,
    columns: GridColDefPro[],
): MapaVisibilidade {
    const vivos = columns.filter(ocultavel).map(column => column.field)

    return Object.entries(mapa).reduce<MapaVisibilidade>(
        (podado, [field, visivel]) => {
            if (vivos.includes(field)) podado[field] = visivel
            return podado
        },
        {},
    )
}

/**
 * Aplica o mapa nas colunas sem mutar a entrada.
 * Campo ausente do mapa (coluna nova num release futuro) mantém o `hide` do colDef.
 */
export function aplicarVisibilidade(
    columns: GridColDefPro[],
    mapa: MapaVisibilidade,
): GridColDefPro[] {
    return columns.map(column => {
        if (!ocultavel(column)) return column

        if (!Object.hasOwn(mapa, column.field)) return column

        const visivel = mapa[column.field]

        return { ...column, hide: !visivel }
    })
}

/**
 * Exibe tudo que o usuário pode esconder. Coluna não-ocultável fica como
 * está: a chave de agrupamento do Ponto Diário é `hide: true` permanente, e
 * revelá-la duplicaria na tela a coluna de nome.
 */
export function mostrarTodas(columns: GridColDefPro[]): GridColDefPro[] {
    return columns.map(column =>
        ocultavel(column) ? { ...column, hide: false } : column,
    )
}
